import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  formatDateLocal,
  formatDateTimeLocal,
} from "@/shared/lib/date-time";
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckSquare,
  Clock,
  Edit,
  Eye,
  FileText,
  HelpCircle,
  Info,
  User,
  Users,
  Workflow,
} from "lucide-react";
import { useState } from "react";
import type { FormDTO } from "../../domain/contracts";
import {
  useFormAnalyticsStats,
  useFormQuery,
  useFormSubmissionTrends,
} from "../api/queries";
import {
  FormAnalysisTour,
  useFormAnalysisTour,
} from "../tour/FormAnalysisTour";

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  under_revision: "ກຳລັງປັບປຸງ",
  discontinued: "ຢຸດໃຊ້ງານ",
  draft: "ຮ່າງ",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  under_revision: "secondary",
  discontinued: "destructive",
  draft: "outline",
};

export function FormAnalysisPage() {
  const nav = useNavigate();
  const { id } = useParams({ from: "/app/forms/$id/analysis" });

  const { data: form, ...result } = useFormQuery(id ?? "");
  const formData = form as FormDTO | undefined;
  const canEdit = useActionPermission([Permissions.forms.update]);
  const tour = useFormAnalysisTour();

  // Fetch analytics statistics
  const { data: statsData } = useFormAnalyticsStats(id ?? "");
  const stats = statsData || {
    totalSubmissions: 0,
    submissionsChange: 0,
    activeUsers: 0,
    usersChange: 0,
    avgCompletionTime: "0m 0s",
    completionChange: 0,
  };

  // State for trend period
  const [trendPeriod, setTrendPeriod] = useState<"7d" | "30d" | "90d" | "1y">(
    "30d",
  );

  // Fetch submission trends (placeholder - will be used when chart is implemented)
  const { data: trendsData } = useFormSubmissionTrends(id ?? "", trendPeriod);

  // Mock data for resources (TODO: Implement API for resources)
  const resources = [
    { name: "Leave Policy Guide", type: "PDF", size: "1.2 MB" },
    { name: "Sample Filled Form", type: "DOCX", size: "450 KB" },
    { name: "Workflow Diagram", type: "XLSX", size: "890 KB" },
  ];

  // Submission trends data - placeholder for chart implementation
  // When chart is ready, use: trendsData?.trends || []
  const submissionTrends = trendsData?.trends || [
    { date: "01 OCT", value: 45 },
    { date: "05 OCT", value: 52 },
    { date: "10 OCT", value: 38 },
    { date: "15 OCT", value: 61 },
    { date: "20 OCT", value: 48 },
    { date: "25 OCT", value: 55 },
    { date: "30 OCT", value: 42 },
  ];

  const maxValue =
    submissionTrends.length > 0
      ? Math.max(...submissionTrends.map((d) => d.value))
      : 100;

  return (
    <>
      <Header />
      <Main>
        <FormAnalysisTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />

        <QueryState
          result={result}
          variant="fullscreen"
          isEmpty={!formData}
          emptyFallback={
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground">ບໍ່ພົບຟອມ</p>
            </div>
          }
        >
          {formData && (
            <div className="space-y-6">
              {/* Back Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  nav({ to: "/app/forms/$id", params: { id: formData.id } })
                }
                className="mb-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບຄືນ
              </Button>

              {/* Form Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="font-bold text-3xl">{formData.name}</h1>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 rounded-full"
                      onClick={tour.startTour}
                    >
                      <HelpCircle className="size-5" />
                    </Button>
                    <Badge variant={statusColors[formData.status]}>
                      {statusLabels[formData.status] || formData.status}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-muted-foreground text-sm">
                    <span>ລະຫັດຟອມ: {formData.id}</span>
                    {formData.categoryName && (
                      <>
                        <span>•</span>
                        <span>ໝວດໝູ່: {formData.categoryName}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>
                      ສ້າງເມື່ອ: {formatDateLocal(formData.createdAt)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() =>
                        nav({
                          to: "/app/forms/$id",
                          params: { id: formData.id },
                        })
                      }
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      ແກ້ໄຂຟອມ
                    </Button>
                  )}
                </div>
              </div>

              {/* Key Statistics */}
              <div
                className="grid grid-cols-1 gap-4 md:grid-cols-3"
                data-tourid="analysis-stats"
              >
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-muted-foreground text-sm">
                          ຈຳນວນການສົ່ງທັງໝົດ
                        </p>
                        <p className="mt-2 font-bold text-3xl">
                          {stats.totalSubmissions.toLocaleString()}
                        </p>
                        <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                          <span>↑{stats.submissionsChange}%</span>
                          <span className="text-muted-foreground">
                            ເມື່ອທຽບກັບເດືອນກ່ອນ
                          </span>
                        </div>
                      </div>
                      <div className="rounded-lg bg-blue-100 p-3">
                        <FileText className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-muted-foreground text-sm">
                          ຜູ້ໃຊ້ທີ່ມີກິດຈະກຳ
                        </p>
                        <p className="mt-2 font-bold text-3xl">
                          {stats.activeUsers.toLocaleString()}
                        </p>
                        <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                          <span>↑{stats.usersChange}%</span>
                          <span className="text-muted-foreground">
                            ພະນັກງານທີ່ແຕກຕ່າງກັນ
                          </span>
                        </div>
                      </div>
                      <div className="rounded-lg bg-green-100 p-3">
                        <Users className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-muted-foreground text-sm">
                          ເວລາສຳເລັດໂດຍສະເລ່ຍ
                        </p>
                        <p className="mt-2 font-bold text-3xl">
                          {stats.avgCompletionTime}
                        </p>
                        <div className="mt-2 flex items-center gap-1 text-red-600 text-sm">
                          <span>↓{Math.abs(stats.completionChange)}%</span>
                          <span className="text-muted-foreground">
                            ໄວກວ່າຄ່າສະເລ່ຍ
                          </span>
                        </div>
                      </div>
                      <div className="rounded-lg bg-orange-100 p-3">
                        <Clock className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Main Content - Two Columns */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Left Column */}
                <div className="space-y-6 lg:col-span-2">
                  {/* Form Instructions & Purpose */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        <CardTitle>ຄຳແນະນຳແລະຈຸດປະສົງຂອງຟອມ</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <h3 className="mb-2 font-semibold">ບໍລິບົດການນຳໃຊ້</h3>
                        <p className="text-muted-foreground text-sm">
                          {formData.description ||
                            "ຟອມນີ້ຈຸດປະສົງໃຫ້ພະນັກງານທຸກປະເພດ. ການຮ້ອງຂໍຄວນສົ່ງລ່ວງໜ້າສຳລັບກິດຈະກຳທີ່ວາງແຜນໄວ້."}
                        </p>
                      </div>

                      <div>
                        <div className="mb-2 flex items-center gap-2">
                          <CheckSquare className="h-4 w-4" />
                          <h3 className="font-semibold">ຂັ້ນຕອນການສົ່ງ</h3>
                        </div>
                        <ul className="ml-6 list-disc space-y-1 text-muted-foreground text-sm">
                          <li>ຕື່ມລາຍລະອຽດທີ່ຈຳເປັນ</li>
                          <li>ເລືອກວັນທີໃຊ້ຕົວເລືອກວັນທີ</li>
                          <li>ແນບເອກະສານສະໜັບສະໜູນຖ້າຈຳເປັນ</li>
                          <li>ກວດສອບສະຫຼຸບກ່ອນສົ່ງສຸດທ້າຍ</li>
                        </ul>
                      </div>

                      <div>
                        <div className="mb-2 flex items-center gap-2">
                          <Workflow className="h-4 w-4" />
                          <h3 className="font-semibold">ຂະບວນການຫຼັງສົ່ງ</h3>
                        </div>
                        <ul className="ml-6 list-disc space-y-1 text-muted-foreground text-sm">
                          <li>ສົ່ງອີເມວອັດຕະໂນມັດໄປຫາຜູ້ຈັດການ</li>
                          <li>ການອະນຸມັດ/ປະຕິເສດຈາກຜູ້ຈັດການ (SLA 48 ຊົ່ວໂມງ)</li>
                          <li>ອັບເດດລະບົບເມື່ອອະນຸມັດ</li>
                          <li>ການແຈ້ງເຕືອນຢືນຢັນສຸດທ້າຍ</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Submission Trends */}
                  <Card data-tourid="analysis-trends">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-5 w-5" />
                          <CardTitle>ແນວໂນ້ມການສົ່ງ</CardTitle>
                        </div>
                        <select
                          className="rounded-md border bg-background px-3 py-1 text-sm"
                          value={trendPeriod}
                          onChange={(e) =>
                            setTrendPeriod(
                              e.target.value as "7d" | "30d" | "90d" | "1y",
                            )
                          }
                        >
                          <option value="7d">7 ວັນລ່າສຸດ</option>
                          <option value="30d">30 ວັນລ່າສຸດ</option>
                          <option value="90d">90 ວັນລ່າສຸດ</option>
                          <option value="1y">1 ປີລ່າສຸດ</option>
                        </select>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex h-64 items-end justify-between gap-2">
                        {submissionTrends.map((trend) => (
                          <div
                            key={trend.date}
                            className="flex flex-1 flex-col items-center gap-2"
                          >
                            <div
                              className="w-full rounded-t bg-blue-500 transition-all hover:bg-blue-600"
                              style={{
                                height: `${(trend.value / maxValue) * 100}%`,
                                minHeight: "4px",
                              }}
                            />
                            <span className="text-muted-foreground text-xs">
                              {trend.date}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Admin Resources */}
                  {/* <Card data-tourid="analysis-resources">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Download className="h-5 w-5" />
                        <CardTitle>ແຫຼ່ງຂໍ້ມູນສຳລັບຜູ້ດູແລ</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {resources.map((resource) => (
                        <div
                          key={resource.name}
                          className="flex items-center justify-between rounded-md border p-3"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {resource.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {resource.type} • {resource.size}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        className="w-full border-dashed"
                        size="sm"
                      >
                        + ເພີ່ມແຫຼ່ງຂໍ້ມູນໃໝ່
                      </Button>
                    </CardContent>
                  </Card> */}

                  {/* Metadata */}
                  <Card>
                    <CardHeader>
                      <CardTitle>ຂໍ້ມູນເພີ່ມເຕີມ</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <User className="h-3 w-3" />
                          <span>ສ້າງໂດຍ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formData.createdByName || formData.createdBy}
                        </p>
                      </div>

                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <Calendar className="h-3 w-3" />
                          <span>ເຜີຍແຜ່ລ່າສຸດ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formatDateTimeLocal(formData.updatedAt)}
                        </p>
                      </div>

                      <div>
                        <div className="mb-1 flex items-center gap-2 text-muted-foreground text-xs">
                          <Eye className="h-3 w-3" />
                          <span>ການເບິ່ງເຫັນ</span>
                        </div>
                        <p className="font-medium text-sm">
                          {formData.departmentName
                            ? `ພຽງແຕ່ ${formData.departmentName}`
                            : "ພະນັກງານພາຍໃນເທົ່ານັ້ນ"}
                        </p>
                      </div>

                      <Button variant="outline" className="w-full" size="sm">
                        ເບິ່ງປະຫວັດການແກ້ໄຂ
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
